/**
 * AutocompleteTextInput — Spec rev60 (2026-08-02)
 *
 * Champ texte avec suggestions basées sur l'historique des valeurs
 * saisies pour le MÊME champ sur le MÊME type de document.
 *
 * Comportement :
 *   • Fetch initial (préfixe vide) au focus → 8 suggestions les plus
 *     fréquentes/récentes.
 *   • Refetch debounced sur saisie (200 ms).
 *   • Sélection via clic OU navigation clavier (↑/↓/Enter/Escape).
 *   • Non-restrictif : l'utilisateur peut valider sa saisie même si
 *     elle n'apparaît pas dans les suggestions.
 *   • Fermeture au blur (délai 120 ms pour permettre le clic).
 *
 * Endpoint : GET /api/documents/field-suggestions
 *   ?type_id=…&field_id=…&q=<prefix>&limit=8
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown, Info } from "lucide-react";
import api from "@/lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const DEBOUNCE_MS = 220;
const MIN_CHARS_FOR_REFETCH = 0; // 0 = même au focus

export default function AutocompleteTextInput({
  value,
  onChange,
  typeId,
  fieldId,
  placeholder = "Saisir la valeur requise…",
  testid,
  disabled = false,
  className = "",
  style = {},
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);

  const canFetch = !!typeId && !!fieldId;

  const fetchSuggestions = async (q) => {
    if (!canFetch) return;
    setLoading(true);
    try {
      const { data } = await api.get("/documents/field-suggestions", {
        params: { type_id: typeId, field_id: fieldId, q: q || "", limit: 8 },
      });
      setSuggestions(Array.isArray(data.items) ? data.items : []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const debouncedFetch = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), DEBOUNCE_MS);
  };

  const handleFocus = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    setOpen(true);
    // Sur le focus, on refetch les suggestions courantes (préfixe = value).
    if (value.length >= MIN_CHARS_FOR_REFETCH) fetchSuggestions(value || "");
  };

  const handleBlur = () => {
    // Delay pour permettre le mousedown sur la liste avant fermeture.
    blurTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    setActiveIdx(-1);
    if (v.length >= MIN_CHARS_FOR_REFETCH) debouncedFetch(v);
    if (!open) setOpen(true);
  };

  const applySuggestion = (s) => {
    if (!s) return;
    onChange(s.value);
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((idx) => Math.min(idx + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((idx) => Math.max(idx - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      applySuggestion(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  const showList = open && canFetch && (suggestions.length > 0 || loading);

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={value || ""}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="h-9 px-2 pr-12 rounded-md border bg-transparent w-full placeholder:text-[#666]"
        style={{ borderColor: "#3A3A3A", background: "#1B1B1B", ...style }}
        data-testid={testid}
        autoComplete="off"
      />
      {/* Spec 2026-02-XX rev64 — tooltip explicatif : les valeurs saisies
          ne deviennent des suggestions futures qu'après clôture du document
          par un admin. */}
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              className="absolute right-7 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity cursor-help"
              style={{ color: "#8A8A8A" }}
              aria-label="Information sur les suggestions"
              data-testid={testid ? `${testid}-info` : undefined}
            >
              <Info size={12} strokeWidth={2.2} />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="end"
            className="max-w-[260px] text-[11px] leading-snug"
            style={{ background: "#141414", border: "1px solid #2A2A2A", color: "#EDEDED" }}
          >
            Les valeurs saisies ici n'apparaîtront comme suggestions
            futures qu'<strong>après la clôture du document par un
            administrateur</strong>.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <span
        className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-60"
        style={{ color: "#5A9EE0" }}
        title="Suggestions basées sur l'historique"
      >
        <ChevronsUpDown size={12} strokeWidth={2.4} />
      </span>
      {showList && (
        <div
          className="absolute left-0 right-0 top-full mt-1 rounded-md shadow-2xl z-50 overflow-hidden"
          style={{ background: "#141414", border: "1px solid #2A2A2A" }}
          data-testid={testid ? `${testid}-suggestions` : undefined}
        >
          {loading && suggestions.length === 0 ? (
            <div className="px-3 py-2 text-[11px] italic text-muted-foreground">Chargement…</div>
          ) : suggestions.length === 0 ? (
            <div className="px-3 py-2 text-[11px] italic text-muted-foreground">Aucune suggestion</div>
          ) : (
            <>
              <div
                className="px-3 py-1 text-[9px] uppercase tracking-widest text-muted-foreground border-b"
                style={{ borderColor: "rgba(255,255,255,0.05)", background: "rgba(255,255,255,0.02)" }}
              >
                Historique — {suggestions.length} suggestion{suggestions.length > 1 ? "s" : ""}
              </div>
              <ul className="max-h-56 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <li key={`${s.value}-${i}`}>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); applySuggestion(s); }}
                      onMouseEnter={() => setActiveIdx(i)}
                      className="w-full text-left px-3 py-1 flex items-center gap-2 text-[12px] transition-colors"
                      style={{ background: "transparent", color: "#EDEDED" }}
                      data-testid={testid ? `${testid}-suggestion-${i}` : undefined}
                    >
                      <span className="inline-flex items-center" style={{ gap: 7, borderRadius: 999, padding: "5px 12px 5px 9px", fontSize: 12, color: "#DFE3EA", background: i === activeIdx ? "#3568B02e" : "#3568B01f", border: "1px solid #3568B047" }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#3568B0", flexShrink: 0 }} />
                        <span className="truncate">{s.value}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
